import { DecisionAction, DecisionDetail } from "../../../models/home";
import { DetailSectionKey, InspectionTarget } from "./home-decision-detail.types";

/** Owns expansion and inspection selection state for the detail view. */
export class HomeDecisionDetailSectionState {
  readonly inspectionCueClass = "decision-detail__section--inspection-cue";
  expandedSections: Record<DetailSectionKey, boolean> = {
    evidence: false, missing: false, history: false, documents: false
  };
  technicalTraceExpanded = false;
  highlightedSection: InspectionTarget = null;
  selectedActionKey: string | null = null;

  syncSelectedAction(actions: readonly DecisionAction[]): void {
    if (!actions.length) {
      this.selectedActionKey = null;
      return;
    }
    if (this.selectedActionKey && actions.some((action) => action.actionKey === this.selectedActionKey)) return;
    this.selectedActionKey = actions[0]?.actionKey ?? null;
  }
  expand(section: DetailSectionKey): void {
    if (!this.expandedSections[section]) this.expandedSections = { ...this.expandedSections, [section]: true };
  }
  reset(): void {
    this.expandedSections = { evidence: false, missing: false, history: false, documents: false };
    this.technicalTraceExpanded = false;
  }
  isExpanded(section: DetailSectionKey | "technical"): boolean {
    return section === "technical" ? this.technicalTraceExpanded : this.expandedSections[section];
  }
  toggle(section: DetailSectionKey | "technical"): void {
    if (section === "technical") {
      this.technicalTraceExpanded = !this.technicalTraceExpanded;
      this.highlightedSection = this.technicalTraceExpanded
        ? "technical"
        : this.highlightedSection === "technical" ? null : this.highlightedSection;
      return;
    }
    const expanded = !this.expandedSections[section];
    this.expandedSections = { ...this.expandedSections, [section]: expanded };
    if (!expanded && this.highlightedSection === section) this.highlightedSection = null;
  }
}

export interface HomeDecisionInspectionElements {
  action?: HTMLElement;
  evidence?: HTMLElement;
  missing?: HTMLElement;
  technical?: HTMLElement;
}

export class HomeDecisionDetailInspectionCoordinator {
  constructor(private readonly state: HomeDecisionDetailSectionState) {}

  resolveTarget(decision: DecisionDetail["decision"] | null, hasSourceTrace: boolean): InspectionTarget {
    if (!decision) return null;
    if (decision.missingInformation.length) return "missing";
    if (decision.evidence.length) return "evidence";
    return hasSourceTrace ? "technical" : null;
  }

  focusTarget(target: InspectionTarget, elements: HomeDecisionInspectionElements): void {
    let element = elements.action;
    if (target === "technical") {
      this.state.technicalTraceExpanded = true;
      element = elements.technical ?? element;
    } else if (target === "evidence") {
      this.state.expand("evidence");
      element = elements.evidence ?? element;
    } else if (target === "missing") {
      this.state.expand("missing");
      element = elements.missing ?? element;
    }
    this.state.highlightedSection = target;
    this.playCue(element);
    element?.scrollIntoView({ behavior: "smooth", block: "center" });
    element?.focus({ preventScroll: true });
  }

  playCue(target?: HTMLElement): void {
    if (!target?.classList) return;
    target.classList.remove(this.state.inspectionCueClass);
    target.offsetWidth;
    target.classList.add(this.state.inspectionCueClass);
  }
}
